import {
	isChartWidgetProps,
	isPivotTableWidgetProps,
	type ChartProps,
	type WidgetProps,
} from '@sisense/sdk-ui';
import { translateChartToJSON } from '@sisense/sdk-ui/analytics-composer';
import type {
	ExtendedChartWidgetProps,
	BaseChartSummary,
} from '../build-chart/types';
import { getBaseChartSummary } from '../build-chart/build-chart-service';
import { getFunctionCallFiltersForLlmSummary } from '../shared/get-filters-json-for-llm-summary';
import { BaseDashboardSummary, ExtendedDashboardProps } from './types';

function resolveChartState(
	widgetProps: ExtendedChartWidgetProps,
): BaseChartSummary | undefined {
	let chartState = getBaseChartSummary(widgetProps);
	if (!chartState) {
		const chartTranslation = translateChartToJSON(
			widgetProps as Partial<ChartProps>,
		);
		if (chartTranslation.success && 'data' in chartTranslation) {
			chartState = {
				...chartTranslation.data,
				...(widgetProps.title && { title: widgetProps.title }),
			};
		}
	}
	return chartState;
}

function optionalDistinctDataSourceTitle(
	widgetProps: WidgetProps,
	defaultDataSourceTitle: string,
): Pick<BaseDashboardSummary['widgets'][number], 'dataSourceTitle'> {
	const dataSource =
		'dataSource' in widgetProps &&
		widgetProps.dataSource &&
		typeof widgetProps.dataSource === 'object'
			? (widgetProps.dataSource as { title?: string })
			: undefined;
	const title = dataSource?.title;
	if (title && title !== defaultDataSourceTitle) {
		return { dataSourceTitle: title };
	}
	return {};
}

function summarizeWidget(
	widgetProps: WidgetProps,
	defaultDataSourceTitle: string,
): BaseDashboardSummary['widgets'][number] {
	const widgetTitle =
		'title' in widgetProps && typeof widgetProps.title === 'string'
			? widgetProps.title
			: undefined;

	const base: BaseDashboardSummary['widgets'][number] = {
		widgetId: widgetProps.id ?? '',
		widgetType: widgetProps.widgetType,
		...(widgetTitle ? { widgetTitle } : {}),
		...optionalDistinctDataSourceTitle(widgetProps, defaultDataSourceTitle),
	};

	if (isChartWidgetProps(widgetProps)) {
		const chartState = resolveChartState(
			widgetProps as ExtendedChartWidgetProps,
		);
		return chartState ? { ...base, chartState } : base;
	}

	if (isPivotTableWidgetProps(widgetProps)) {
		return base;
	}

	return base;
}

/**
 * Get a base dashboard summary from dashboard props
 * This is used by the engine to create a summary for LLM consumption
 */
export const getBaseDashboardSummary = (
	dashboardProps: ExtendedDashboardProps,
	dataSourceTitle: string,
): BaseDashboardSummary => {
	return {
		fusionDashboardId: dashboardProps.id,
		dashboardTitle: dashboardProps.title,
		defaultDataSourceTitle: dataSourceTitle,
		filters: getFunctionCallFiltersForLlmSummary(dashboardProps.filters),
		widgets: dashboardProps.widgets.map((widgetProps) =>
			summarizeWidget(widgetProps, dataSourceTitle),
		),
	};
};
